import { useEffect, useCallback, memo } from 'react'
import { Feed, AnyContent, RangeFilter, ContentItem, ALL_FILTER } from 'interfaces'
import FetchFeedPageContent from 'components/Fetch/FetchFeedPageContent'
import { FetchableProps, ContentGridView } from 'components/ContentGridView'
import { STOCK_ALL_FILTER } from 'interfaces/Content/StockContentTypes'

import isEqual from 'react-fast-compare'
import { FilterType } from 'shared'

export interface ScrollConfiguration {
  element?: Element
  useWindow?: boolean
}

export interface FeedBricksViewProps {
  feed: Feed
  filter: FilterType
  scroll?: ScrollConfiguration
  itemWidth?: number
  range?: RangeFilter
  skipContentFeatureChecks?: boolean
  onItemPinned?(feed: Feed, content: AnyContent): void
  onItemShared?(feed: Feed, content: AnyContent): void | boolean
  onGotoClicked?(feed: Feed, content: AnyContent, by: string): void
  onRangeChanged?(feed: Feed, range: RangeFilter): void
  onFilterChange(filter: FilterType): void
  onContentItemClick?(content: ContentItem): void
}

const FeedBricksView = ({
  feed,
  filter,
  scroll,
  itemWidth,
  range,
  skipContentFeatureChecks,
  onItemPinned,
  onItemShared,
  onGotoClicked,
  onRangeChanged,
  onFilterChange,
  onContentItemClick
}: FeedBricksViewProps) => {
  const validateFilter = useCallback(() => {
    const allowed = feed.sources
    let validFilterForSource: FilterType = allowed.find(f => f === filter) || feed.sources[0]
    if (filter === ALL_FILTER) {
      validFilterForSource = ALL_FILTER
    } else if (filter === STOCK_ALL_FILTER) {
      validFilterForSource = STOCK_ALL_FILTER
    }

    if (validFilterForSource !== filter) {
      onFilterChange(validFilterForSource)
    }
  }, [feed, filter, onFilterChange])

  useEffect(() => {
    validateFilter()
  }, [validateFilter])

  const handleItemPinned = useCallback((content: AnyContent) => {
    if (onItemPinned) {
      onItemPinned(feed, content)
    }
  }, [feed, onItemPinned])

  const handleItemShared = useCallback((content: AnyContent) => {
    if (onItemShared) {
      return onItemShared(feed, content)
    }
  }, [feed, onItemShared])

  const handleGotoClicked = useCallback((content: AnyContent, by: string) => {
    if (onGotoClicked) {
      onGotoClicked(feed, content, by)
    }
  }, [feed, onGotoClicked])

  const handleFeedClicked = useCallback((content: AnyContent) => {
    handleGotoClicked(content, 'attribution')
  }, [handleGotoClicked])

  const handleRangeChanged = useCallback((newRange: RangeFilter) => {
    if (onRangeChanged) {
      return onRangeChanged(feed, newRange)
    }
    return false
  }, [feed, onRangeChanged])

  const createLoadMore = useCallback((props: FetchableProps) => {
    console.log('[feed bricks] load more', props)
    return <FetchFeedPageContent {...props} feed={feed} key={`fetch[${feed.id}.${props.page}:${props.range}:${props.sortBy}]`} />
  }, [feed])

  return (
    <ContentGridView
      sourceId={feed.uniqueSource}
      filter={filter}
      scroll={scroll}
      itemWidth={itemWidth}
      range={range}
      feedType={feed.type}
      contentTypes={feed.sources}
      sourcesHash={feed.id + ''}
      skipContentCardsFeatureChecks={skipContentFeatureChecks}
      preventTopOverflow={true}
      onFilterChange={onFilterChange}
      onRangeChanged={handleRangeChanged}
      onItemShared={handleItemShared}
      onItemPinned={handleItemPinned}
      onSourceClick={handleGotoClicked}
      onFeedClick={handleFeedClicked}
      onContentItemClick={onContentItemClick}
    />
  )
}

export default memo(FeedBricksView, (prevProps, nextProps) => isEqual(prevProps, nextProps))
